import type { components } from '@connected/api-client/api.generated';

import { api } from '@connected/api-client';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { toPascalCase } from '@connected/api-client/lib/utils';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';

import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';

export function useCreateRisoMeasurement({
    electricalProjectVersionId,
}: {
    electricalProjectVersionId: string;
}) {
    const mutation = api.useMutation('post', '/api/v2/electrical/projects/riso/measurements/create', {
        onMutate: async (variables) => {
            const listMeasurementsQueryOptions = elektraAppQueryOptions.project.risoMeasurementOptions({
                electricalProjectVersionId,
            });
            const body = toPascalCase(variables!.body!);
            await queryClient.cancelQueries(listMeasurementsQueryOptions);

            const previousData = queryClient.getQueryData<components['schemas']['ListElectricalProjectRisoMeasurementQueryResult']>(listMeasurementsQueryOptions.queryKey);

            queryClient.setQueryData<components['schemas']['ListElectricalProjectRisoMeasurementQueryResult']>(
                listMeasurementsQueryOptions.queryKey,
                {
                    ...previousData,
                    Measurements: [
                        ...(previousData?.Measurements || []),
                        {
                            ...body,
                        },
                    ],
                    Count: (previousData?.Count || 0) + 1,
                },
            );

            return { previousData };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.risoMeasurementOptions({
                electricalProjectVersionId,
            }));
        },
        scope: {
            id: electricalProjectVersionId,
        },
    });

    return { mutation };
}

export function useDeleteRisoMeasurement({
    electricalProjectVersionId,
}: {
    electricalProjectVersionId: string;
}) {
    const mutation = api.useMutation('delete', '/api/v2/electrical/projects/riso/measurements/delete', {
        onMutate: async (variables) => {
            await queryClient.cancelQueries(elektraAppQueryOptions.project.risoMeasurementOptions({
                electricalProjectVersionId,
            }));

            const previousData = queryClient.getQueryData<components['schemas']['ListElectricalProjectRisoMeasurementQueryResult']>(
                elektraAppQueryOptions.project.risoMeasurementOptions({
                    electricalProjectVersionId,
                }).queryKey,
            );

            const updatedMeasurements = previousData?.Measurements?.filter(
                measurement => measurement.ElectricalProjectRisoMeasurementId !== variables!.params!.query.ElectricalProjectRisoMeasurementId,
            ) || [];

            queryClient.setQueryData<components['schemas']['ListElectricalProjectRisoMeasurementQueryResult']>(
                elektraAppQueryOptions.project.risoMeasurementOptions({
                    electricalProjectVersionId,
                }).queryKey,
                {
                    ...previousData,
                    Measurements: updatedMeasurements,
                    Count: (previousData?.Count || 0) - 1,
                },
            );

            return { previousData };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.risoMeasurementOptions({
                electricalProjectVersionId,
            }));
        },
        scope: {
            id: electricalProjectVersionId,
        },
    });

    return { mutation };
}

export function useRisoMeasurementPatcher({
    electricalProjectVersionId,
    electricalProjectRisoMeasurementId,
}: {
    electricalProjectVersionId: string;
    electricalProjectRisoMeasurementId: string;
}) {
    const patcher = usePatcher('/api/v2/electrical/projects/riso/measurements/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body!);

            const risoMeasurementQueryOptions = elektraAppQueryOptions.project.risoMeasurementOptions({
                electricalProjectVersionId,
            });

            await queryClient.cancelQueries(risoMeasurementQueryOptions);

            const previousData = queryClient.getQueryData<components['schemas']['ListElectricalProjectRisoMeasurementQueryResult']>(risoMeasurementQueryOptions.queryKey);

            const newData: components['schemas']['ListElectricalProjectRisoMeasurementQueryResult'] = {
                ...previousData,
                Measurements: previousData?.Measurements?.map((measurement) => {
                    if (measurement.ElectricalProjectRisoMeasurementId === electricalProjectRisoMeasurementId) {
                        return {
                            ...measurement,
                            ...body,
                        };
                    }
                    return measurement;
                }) || [],
            };

            queryClient.setQueryData<components['schemas']['ListElectricalProjectRisoMeasurementQueryResult']>(
                risoMeasurementQueryOptions.queryKey,
                newData,
            );

            logger.debug({
                message: 'onMutate(risoMeasurementQueryOptions)',
                path: '/api/v2/electrical/projects/riso/measurements/patch',
                risoMeasurement: {
                    previousData,
                    newData,
                    queryKey: risoMeasurementQueryOptions.queryKey,
                    body,
                },
            });

            return { previousData };
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.risoMeasurementOptions({
                electricalProjectVersionId,
            }));
        },
        onError: async (error, variables, context) => {
            logger.error({
                message: 'Error while patching electrical project distribution board device command',
                path: '/api/v2/electrical/projects/riso/measurements/patch',
                risoMeasurement: {
                    variables,
                    context,
                },
                error,
            }, {
                apiHttpMethod: 'Patch',
                apiUrl: '/api/v2/electrical/projects/riso/measurements/patch',
            });
        },
        scope: {
            id: electricalProjectVersionId,
        },
    }, { body: { ElectricalProjectRisoMeasurementId: electricalProjectRisoMeasurementId } });

    return patcher;
}

export function useUpdateRisoMeasurementOrder({
    electricalProjectVersionId,
}: {
    electricalProjectVersionId: string;
}) {
    const mutation = api.useMutation('patch', '/api/v2/electrical/projects/riso/measurements/patch', {
        onMutate: async (variables) => {
            const body = toPascalCase(variables!.body!);

            const risoMeasurementQueryOptions = elektraAppQueryOptions.project.risoMeasurementOptions({
                electricalProjectVersionId,
            });

            await queryClient.cancelQueries(risoMeasurementQueryOptions);

            const previousData = queryClient.getQueryData<components['schemas']['ListElectricalProjectRisoMeasurementQueryResult']>(risoMeasurementQueryOptions.queryKey);

            const newData: components['schemas']['ListElectricalProjectRisoMeasurementQueryResult'] = {
                ...previousData,
                Measurements: previousData?.Measurements?.map((measurement) => {
                    if (measurement.ElectricalProjectRisoMeasurementId === body.ElectricalProjectRisoMeasurementId) {
                        return {
                            ...measurement,
                            Order: body.Order,
                        };
                    }
                    return measurement;
                }) || [],
            };

            queryClient.setQueryData<components['schemas']['ListElectricalProjectRisoMeasurementQueryResult']>(
                risoMeasurementQueryOptions.queryKey,
                newData,
            );

            return { previousData };
        },
        onError: (error, _variables, context) => {
            const risoMeasurementQueryOptions = elektraAppQueryOptions.project.risoMeasurementOptions({
                electricalProjectVersionId,
            });

            const ctx = context as { previousData?: components['schemas']['ListElectricalProjectRisoMeasurementQueryResult'] } | undefined;
            if (ctx?.previousData) {
                queryClient.setQueryData(
                    risoMeasurementQueryOptions.queryKey,
                    ctx.previousData,
                );
            }

            logger.error({
                message: 'Error while updating riso measurement order',
                error,
            });
        },
        onSettled: async () => {
            await queryClient.invalidateQueries(elektraAppQueryOptions.project.risoMeasurementOptions({
                electricalProjectVersionId,
            }));
        },
        scope: {
            id: electricalProjectVersionId,
        },
    });

    async function updateOrder(updates: Array<{ ElectricalProjectRisoMeasurementId: string; Order: number }>) {
        // Send individual mutations for each measurement order update
        await Promise.all(
            updates.map(update =>
                mutation.mutateAsync({
                    body: {
                        ElectricalProjectRisoMeasurementId: update.ElectricalProjectRisoMeasurementId,
                        Order: update.Order,
                    },
                }),
            ),
        );
    }

    return { updateOrder, mutation };
}
